<template>
  <div class="md-rank">
    <div v-if="!items.length" class="md-rank__empty">{{ $t('mobileDash.noData') }}</div>
    <template v-else>
      <component
          :is="selectable ? 'button' : 'div'"
          v-for="(item, i) in visibleItems" :key="item.key || i"
          :type="selectable ? 'button' : null"
          class="md-rank__row"
          :class="{ 'md-rank__row--tap': selectable }"
          @click="selectable && $emit('select', item)"
      >
        <span class="md-rank__pos" :class="{ 'md-rank__pos--top': i === 0 }">{{ i + 1 }}</span>
        <span class="md-rank__main">
          <span class="md-rank__label">{{ item.label }}</span>
          <span v-if="item.stats && item.stats.length" class="md-rank__stats">
            <span
                v-for="(stat, si) in item.stats" :key="si"
                class="md-rank__stat"
                :class="`md-rank__stat--${stat.tone || 'default'}`"
            >
              <b>{{ stat.value }}</b><span class="md-rank__stat-label">{{ stat.label }}</span>
            </span>
          </span>
          <span v-else-if="item.sub" class="md-rank__sub">{{ item.sub }}</span>
        </span>
        <span class="md-rank__right">
          <span class="md-rank__value">{{ item.value }}</span>
          <span v-if="item.delta != null" class="md-rank__delta" :class="item.delta >= 0 ? 'is-up' : 'is-down'">
            {{ item.delta >= 0 ? '+' : '' }}{{ item.delta.toFixed(1) }}%
          </span>
        </span>
      </component>
      <button v-if="items.length > limit" type="button" class="md-rank__more" @click="expanded = !expanded">
        {{ expanded ? $t('mobileDash.showLess') : $t('mobileDash.viewAll', { count: items.length }) }}
      </button>
    </template>
  </div>
</template>

<script>
export default {
  name: 'MdRankList',
  props: {
    // [{ key, label, sub, value (formatted), delta (number|null), raw }]
    items: { type: Array, default: () => [] },
    limit: { type: Number, default: 5 },
    // Rows become tappable and emit 'select' with the item
    selectable: { type: Boolean, default: false },
  },
  data: () => ({ expanded: false }),
  computed: {
    visibleItems() {
      return this.expanded ? this.items : this.items.slice(0, this.limit);
    },
  },
  watch: {
    items() {
      this.expanded = false;
    },
  },
};
</script>

<style scoped>
.md-rank {
  background: var(--md-card-bg, #fff);
  border: 1px solid var(--md-card-border, rgba(34, 41, 47, 0.08));
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(34, 41, 47, 0.06), 0 5px 12px rgba(34, 41, 47, 0.1);
  overflow: hidden;
}
.dark-layout .md-rank {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.28), 0 7px 16px rgba(0, 0, 0, 0.22);
}
.md-rank__empty {
  padding: 1.1rem;
  font-size: 12.5px;
  text-align: center;
  color: var(--md-muted, #8a919c);
}
.md-rank__row {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.62rem 0.85rem;
}
.md-rank__row + .md-rank__row {
  border-top: 1px solid var(--md-card-border, rgba(34, 41, 47, 0.06));
}
button.md-rank__row {
  width: 100%;
  text-align: left;
  background: transparent;
  border: none; /* separators come from the sibling border-top rule (higher specificity) */
}
.md-rank__row--tap:active { background: rgba(138, 145, 156, 0.08); }
.md-rank__pos {
  flex: 0 0 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  color: var(--md-muted, #8a919c);
  background: rgba(138, 145, 156, 0.12);
}
.md-rank__pos--top {
  color: #b07d1a;
  background: rgba(255, 190, 61, 0.18);
}
.md-rank__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.md-rank__label {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--md-strong, #24292f);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.md-rank__sub {
  font-size: 11px;
  color: var(--md-muted, #8a919c);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.md-rank__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 3px;
}
.md-rank__stat {
  display: inline-flex;
  align-items: center;
  gap: 3px; /* space between the number and its label — text spaces collapse in flex */
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  padding: 3px 7px;
  border-radius: 999px;
  white-space: nowrap;
}
.md-rank__stat b { font-weight: 800; }
.md-rank__stat--default { background: rgba(138, 145, 156, 0.14); color: #6c757d; }
.md-rank__stat--ftd { background: rgba(115, 103, 240, 0.14); color: #6a5fe0; }
.md-rank__stat--leads { background: rgba(0, 158, 217, 0.13); color: #0f89b8; }
.md-rank__stat--std { background: rgba(40, 199, 111, 0.14); color: #1f9d61; }
.md-rank__stat--conv { background: rgba(255, 159, 67, 0.16); color: #c47b28; }
.md-rank__stat--in { background: rgba(40, 199, 111, 0.14); color: #1f9d61; }
.md-rank__stat--out { background: rgba(234, 84, 85, 0.13); color: #d64545; }

.dark-layout .md-rank__stat--default { color: #a6adb8; }
.dark-layout .md-rank__stat--ftd { color: #9d94f5; }
.dark-layout .md-rank__stat--leads { color: #4fc3e8; }
.dark-layout .md-rank__stat--std { color: #48d989; }
.dark-layout .md-rank__stat--conv { color: #ffb976; }
.dark-layout .md-rank__stat--in { color: #48d989; }
.dark-layout .md-rank__stat--out { color: #f08182; }
.md-rank__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.md-rank__value {
  font-size: 13.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--md-strong, #24292f);
}
.md-rank__delta { font-size: 11px; font-weight: 600; }
.md-rank__delta.is-up { color: #1f9d61; }
.md-rank__delta.is-down { color: #d64545; }
.md-rank__more {
  display: block;
  width: 100%;
  padding: 0.55rem;
  font-size: 12px;
  font-weight: 600;
  color: var(--primary, #7367f0);
  background: transparent;
  border: none;
  border-top: 1px solid var(--md-card-border, rgba(34, 41, 47, 0.06));
}

.dark-layout .md-rank {
  --md-card-bg: #283046;
  --md-card-border: #3b4253;
  --md-strong: #d0d2d6;
}
</style>
